import {
  initVersionedSchema,
  mergeSchemas,
  toTextContent,
} from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  IStaffer,
  type IClinicalNote,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toISODate,
  toMomentTz,
  toNamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, groupBy, sortBy } from 'lodash';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { isClinicalNoteCode } from '../../../mappings/item-code-mapping-helpers';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { ADAItemSourceEntity } from '../../source/entities/ada-item';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientTreatmentNote,
  PatientTreatmentNoteSourceEntity,
} from '../../source/entities/patient-treatment-note';
import { D4WItemCodeToNoteMappingHandler } from '../mappings/item-code-to-note';
import { D4WItemCodeMappingHandler } from '../mappings/item-codes';
import { D4WStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';

interface ID4WClinicalNoteJobData extends IClinicalNoteJobData<D4WPatient> {
  sourceItemCodesToNotes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeNoteType>
  >[];
}

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<D4WPatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    clinicalNotes: new PatientTreatmentNoteSourceEntity(),
  };

  customMappings = {
    staff: new D4WStafferMappingHandler(),
    itemCodes: new D4WItemCodeMappingHandler(),
    itemCodesToNotes: new D4WItemCodeToNoteMappingHandler(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<ID4WClinicalNoteJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const sourceItemCodesToNotes$ =
      this.customMappings.itemCodesToNotes.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
      sourceItemCodesToNotes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [staff, practitioners, sourceItemCodes, sourceItemCodesToNotes],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            practitioners,
            sourceItemCodes,
            sourceItemCodesToNotes,
            sourcePatient,
            destinationEntity,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ID4WClinicalNoteJobData
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const notes = await this.sourceEntities.clinicalNotes.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      const clinicalNotes = await this.buildClinicalNotes(
        migration,
        notes,
        translationMap,
        data
      );

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async buildClinicalNotes(
    migration: WithRef<IPracticeMigration>,
    notes: IGetRecordResponse<D4WPatientTreatmentNote>[],
    translationMap: TranslationMapHandler,
    data: ID4WClinicalNoteJobData
  ): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
    const sourceEntity = new ADAItemSourceEntity();

    const filteredNotes = notes.filter((note) => {
      const sourceId = sourceEntity
        .getSourceRecordId({ id: note.data.item_id })
        .toString();
      return isClinicalNoteCode(
        note.data.item_code,
        sourceId,
        data.sourceItemCodes,
        data.sourceItemCodesToNotes
      );
    });

    const clinicalNotes = await resolveSequentially(
      sortBy(filteredNotes, 'data.data.created_at'),
      async (note) => {
        const owner = await getClinicalNotePractitioner(
          note.data.provider_id.toString(),
          data.staff,
          translationMap
        );
        return {
          sourceIdentifier: note.record.uid,
          ...ClinicalNote.init({
            owner,
            content: initVersionedSchema([
              toTextContent(
                `From ${note.data.item_code} ${note.data.tooth_ref ?? ''}${
                  note.data.tooth_surface ?? ''
                }`
              ),
              toTextContent(note.data.content || ' '),
            ]),
            createdAt: note.record.translations.createdAt,
            immutable: true,
            recordDate: note.record.translations.treatmentDate
              ? note.record.translations.treatmentDate
              : toISODate(
                  toMomentTz(
                    note.record.translations.createdAt,
                    migration.configuration.timezone
                  )
                ),
          }),
        };
      }
    );

    return compact(
      Object.values(
        groupBy(
          clinicalNotes,
          (clinicalNote) =>
            `${clinicalNote.recordDate}-${clinicalNote.owner.ref.id}`
        )
      ).map((groupedNotes) => {
        if (!groupedNotes.length) {
          return;
        }

        return groupedNotes.reduce(
          (combinedClinicalNote, clinicalNote) => ({
            ...combinedClinicalNote,
            content: mergeSchemas([
              combinedClinicalNote.content,
              clinicalNote.content,
            ]),
          }),
          { ...groupedNotes[0], content: initVersionedSchema() }
        );
      })
    );
  }
}

async function getClinicalNotePractitioner(
  practitionerId: string,
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  translationMap: TranslationMapHandler
): Promise<INamedDocument<IStaffer>> {
  const practitionerMap =
    staff.find((staffer) => staffer.sourceIdentifier === practitionerId)
      ?.destinationIdentifier ||
    ((await translationMap.getBySource(practitionerId, STAFFER_RESOURCE_TYPE))
      ?.destinationIdentifier as DocumentReference<IStaffer> | undefined);

  if (!practitionerMap) {
    throw new Error(`Couldn't find practitioner with id ${practitionerId}`);
  }

  const creator = await Firestore.getDoc(practitionerMap);
  return stafferToNamedDoc(creator);
}
